/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchEmployees } from "../../../store/slices/employeeSlice";
import { fetchCompaniesByHierarchy } from "../../../store/slices/companySlice";
import { importEmployees } from "../../../store/slices/employeeImportSlice";
import EmployeeUploadModal from "../../components/EmployeeUploadModal";
import { SearchBar } from "../../components/SearchBar";
import { fetchBranchesByHierarchy } from "../../../store/slices/branchSlice";
import { fetchDepartments } from "../../../store/slices/departmentSlice";
import Layout from "../../components/Layout";
import { fetchAllLeaveRequests } from "../../../store/slices/LeaveManagement/employeeLeaveSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { leaveService } from "../../../api/LeaveService/leaveService";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const EmployeeLeaveApprovalView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
    const company = localStorage.getItem("branchId");
    const queryClient = useQueryClient();


  /* ========================================================================== */
  /* 1. REDUX & LOCAL STORAGE SELECTORS                                        */
  /* ========================================================================== */
  const employeeState = useSelector((state) => state.employee);
  const employeeListData = useMemo(() => {
    if (!employeeState.list) return [];
    if (Array.isArray(employeeState.list)) return employeeState.list;
    return employeeState.list.data || [];
  }, [employeeState.list]);
  const currentData = employeeListData;

 const { list: employeeLeave } = useSelector((state) => state.employeeLeave);

  const { list: branchList } = useSelector((state) => state.branch);
  const { list: allOrgs = [] } = useSelector((state) => state.companyTypeList);
  const { list: allBranches = [] } = useSelector((state) => state.branch);

  const storedCompanyId = localStorage.getItem("companyId");
  const storedBranchId = localStorage.getItem("branchId");

useEffect(() => {
  if (storedCompanyId) {
    console.log("triggered");
    dispatch(fetchAllLeaveRequests({ 
      companyId: storedCompanyId, 
      status: 'pending',
      branchId: storedBranchId 
    }));
  }
}, [dispatch, storedCompanyId, storedBranchId]);
  /* ========================================================================== */
  /* 2. ALL LOCAL COMPONENT STATES (Moved up here to prevent hoisting errors)  */
  /* ========================================================================== */
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "firstName",
    direction: "asc",
  });

  const isMultipleBranches = allBranches.length > 1;

  const branchOptions = isMultipleBranches
    ? [
        { value: storedBranchId, label: "All Branch" },
        ...allBranches
          .filter(
            (branch) =>
              branch.id !== "All Branch" &&
              branch.id !== storedBranchId &&
              !/all/i.test(branch.branchName || branch.name),
          )
          .map((branch) => ({
            value: branch.id,
            label: branch.branchName || branch.name,
          })),
      ]
    : [
        ...allBranches.map((branch) => ({
          value: branch.id,
          label: branch.branchName || branch.name,
        })),
      ];

  const isMultipleCompanies = allOrgs.length > 1;

  const companyOption = isMultipleCompanies
    ? [
        // 🌟 1. Dedicated "All Company" option with a clean blank/all value
        { value: "", label: "All Company" },

        // 🏢 2. List all individual companies with their true names and IDs
        ...allOrgs
          // .filter((org) =>
          //   org.id !== "All Company" &&
          //   !/all/i.test(org.companyName || org.name || (typeof org === "string" ? org : ""))
          // )
          .map((org) => ({
            value: org.id,
            label: org.companyName || org.name || org,
          })),
      ]
    : [
        // 🏢 Case B: Only 1 company exists -> Show its specific name directly
        ...allOrgs.map((org) => ({
          value: org.id,
          label: org.companyName || org.name || org,
        })),
      ];

  const filterSchema = [
    { key: "name", label: "Name", type: "text", placeholder: "Search Name..." },
    {
      key: "department",
      label: "Department",
      type: "text",
      placeholder: "Search Dept...",
    },
    {
      key: "branch",
      label: "Branch",
      defaultValue: storedBranchId,
      type: "select",
      options: branchOptions,
    },
    {
      key: "company",
      label: "Company",
      defaultValue: "All",
      type: "select",
      options: companyOption,
    },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        "Active",
        "Incomplete",
        "Validated",
        "Resigned",
        "Deceased",
        "Notice Period",
        "Terminated",
        "Awol",
      ],
    },
  ];

  // Initialize your filters state block using the schema
  const [filters, setFilters] = useState(() => {
    return filterSchema.reduce((initialObj, field) => {
      initialObj[field.key] =
        field.defaultValue !== undefined ? field.defaultValue : "";
      return initialObj;
    }, {});
  });

  const [selectedCompanyId, setSelectedCompanyId] = useState(
    localStorage.getItem("companyId") || "",
  );
  const [selectedBranchId, setSelectedBranchId] = useState(
    localStorage.getItem("branchId") || "",
  );
  const [preview, setPreview] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const status = employeeState.status;
  const error = employeeState.error;

const serverMeta = useMemo(() => {
  // 1. Calculate length safely
  const length = Array.isArray(employeeLeave) ? employeeLeave.length : 0;

  // 2. Since your API data is a flat array (no meta object), 
  // we calculate pagination from the array length directly.
  return {
    total: length,
    totalPages: Math.max(1, Math.ceil(length / itemsPerPage)),
    page: 1, // Defaulting to 1 as the data is a single flat list
  };
}, [employeeLeave, itemsPerPage]);

  const totalPages = serverMeta.totalPages;
  const totalItems = serverMeta.total;
  const isLoading = status === "loading" || isImporting;
  /* ========================================================================== */
  /* UTILITY HELPERS (Status styling and text casing)                          */
  /* ========================================================================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

const getStatusStyle = (status) => {
  const s = status?.toLowerCase();

  if (s === 'approved')
    return { bg: "bg-success", text: "text-success", border: "border-success" };

  if (s === 'rejected')
    return { bg: "bg-danger", text: "text-danger", border: "border-danger" };

  if (s === 'pending')
    return { bg: "bg-warning", text: "text-warning", border: "border-warning" };

  // This covers 'cancelled' and any unexpected values
  return {
    bg: "bg-secondary",
    text: "text-secondary",
    border: "border-secondary",
  };
};

  /* ========================================================================== */
  /* 3. LIFECYCLE EFFECTS & HANDLERS (Safely references states defined above)  */
  /* ========================================================================== */
  // 🚀 FIXED: This now ONLY fires on initial load, or when explicit pagination triggers shift.
  // It completely ignores raw text/dropdown input changes until you want it to!
  useEffect(() => {
    const cleanFilters = {};

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (
        value !== undefined &&
        value !== null &&
        value !== "All" &&
        value !== "both" &&
        String(value).trim() !== ""
      ) {
        const trimmedValue = String(value).trim();
        if (key === "branch") cleanFilters.branchId = trimmedValue;
        else if (key === "company") cleanFilters.companyId = trimmedValue;
        else if (key === "client") cleanFilters.clientId = trimmedValue;
        else if (key === "role") cleanFilters.roleId = trimmedValue;
        else cleanFilters[key] = trimmedValue;
      }
    });

    const fetchPayload = {
      ...cleanFilters,
      companyId: selectedCompanyId || storedCompanyId,
      page: 1, // Reset safely to page 1 on a fresh company mount switch
      limit: itemsPerPage,
    };

    dispatch(fetchEmployees(fetchPayload));
    setCurrentPage(1);

    // eslint-disable-next-line
  }, [dispatch, selectedCompanyId, storedCompanyId]);

  useEffect(() => {
    if (storedCompanyId) {
      dispatch(fetchCompaniesByHierarchy({ companyId: storedCompanyId }));
      dispatch(fetchDepartments({ companyId: storedCompanyId }));
    }
  }, [dispatch, storedCompanyId]);

  useEffect(() => {
    dispatch(fetchBranchesByHierarchy({ branchId: storedBranchId }));
  }, [dispatch, storedBranchId]);


const handleSearch = () => {
    const cleanFilters = {};

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (
        value !== undefined &&
        value !== null &&
        value !== "All" &&
        value !== "both" &&
        String(value).trim() !== ""
      ) {
        const trimmedValue = String(value).trim();

        if (key === "branch") {
          cleanFilters.branchId = trimmedValue;
        } else if (key === "company") {
          cleanFilters.companyId = trimmedValue;
        } else if (key === "client") {
          cleanFilters.clientId = trimmedValue;
        } else if (key === "role") {
          cleanFilters.roleId = trimmedValue;
        } else {
          cleanFilters[key] = trimmedValue;
        }
      }
    });

    if (!cleanFilters.companyId && storedCompanyId) {
      cleanFilters.companyId = storedCompanyId;
    }

    if (!cleanFilters.branchId && storedBranchId) {
      cleanFilters.branchId = storedBranchId;
    }

    setCurrentPage(1);

    const searchPayload = {
      ...cleanFilters,
      status: cleanFilters.status || 'pending', // Keep or fall back to status filter
      page: 1,
      limit: itemsPerPage,
    };
    
    console.log("here search payload" + JSON.stringify(searchPayload));
    
    // Dispatch the correct leave action with the store/branch IDs included
    dispatch(fetchAllLeaveRequests(searchPayload));
  };


  const handlePageChange = useCallback(
    (targetPage, overrideLimit = null) => {
      setCurrentPage(targetPage);

      const currentLimit =
        overrideLimit !== null ? overrideLimit : itemsPerPage;
      const cleanFilters = {};

      Object.keys(filters).forEach((key) => {
        const value = filters[key];

        if (
          value !== undefined &&
          value !== null &&
          value !== "All" &&
          value !== "both" &&
          String(value).trim() !== ""
        ) {
          const trimmedValue = String(value).trim();

          if (key === "branch") {
            cleanFilters.branchId = trimmedValue;
          } else if (key === "company") {
            cleanFilters.companyId = trimmedValue;
          } else if (key === "client") {
            cleanFilters.clientId = trimmedValue;
          } else if (key === "role") {
            cleanFilters.roleId = trimmedValue;
          } else {
            cleanFilters[key] = trimmedValue;
          }
        }
      });

      // If no explicit company is inside filters, make sure it retains the active contextual selection
      if (!cleanFilters.companyId && selectedCompanyId) {
        cleanFilters.companyId = selectedCompanyId;
      }

      const fetchParams = {
        ...cleanFilters,
        page: targetPage,
        limit: currentLimit,
      };

      dispatch(fetchEmployees(fetchParams));
    },
    // ✅ Keeps dependencies clean and structured
    [dispatch, filters, itemsPerPage, selectedCompanyId],
  );
  // useEffect(() => {
  //   handlePageChange(1);
  // }, [handlePageChange]);

  // useEffect(() => {
  //   // setCurrentPage((prevPage) => {
  //   //   if (serverMeta.page !== prevPage) {
  //   //     return serverMeta.page;
  //   //   }
  //   //   return prevPage;
  //   // });
  //       // dispatch(fetchBranchesByCompanyId(storedCompanyId));

  // }, [serverMeta.page]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return (
        <i
          className="bi bi-arrow-down-up ms-2 opacity-25"
          style={{ fontSize: "0.6rem" }}
        ></i>
      );
    return sortConfig.direction === "asc" ? (
      <i
        className="bi bi-sort-down ms-2 text-primary"
        style={{ fontSize: "0.7rem" }}
      ></i>
    ) : (
      <i
        className="bi bi-sort-up ms-2 text-primary"
        style={{ fontSize: "0.7rem" }}
      ></i>
    );
  };

  /* ===================== DATA MUTATION FILTER / SORT ===================== */
  // const processedEmployees = useMemo(() => {
  //   let items = [...employeeListData];

  //   items = items.filter((emp) => {
  //     const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase();
  //     const dept = (emp.department?.departmentName || '').toLowerCase();
  //     const branch = (emp.branch?.branchName || '').toLowerCase();
  //     const comp = (emp.company?.companyName || '').toLowerCase();
  //     const empStatus = (emp.employeeStatus || 'active').toLowerCase();

  //     const matchesSearch = fullName.includes(filters.name.toLowerCase()) &&
  //                           dept.includes(filters.department.toLowerCase()) &&
  //                           branch.includes(filters.branch.toLowerCase()) &&
  //                           comp.includes(filters.company.toLowerCase());

  //     const matchesStatus = filters.status === 'All' || empStatus === filters.status.toLowerCase();

  //     return matchesSearch && matchesStatus;
  //   });

  //   items.sort((a, b) => {
  //     let aV, bV;
  //     const key = sortConfig.key;
  //     if (key === 'department') aV = (a.department?.departmentName || '').toLowerCase();
  //     else if (key === 'company') aV = (a.company?.companyName || '').toLowerCase();
  //     else if (key === 'branch') aV = (a.branch?.branchName || '').toLowerCase();
  //     else aV = (a[key] || '').toString().toLowerCase();

  //     if (key === 'department') bV = (b.department?.departmentName || '').toLowerCase();
  //     else if (key === 'company') bV = (b.company?.companyName || '').toLowerCase();
  //     else if (key === 'branch') bV = (b.branch?.branchName || '').toLowerCase();
  //     else bV = (b[key] || '').toString().toLowerCase();

  //     if (aV < bV) return sortConfig.direction === 'asc' ? -1 : 1;
  //     if (aV > bV) return sortConfig.direction === 'asc' ? 1 : -1;
  //     return 0;
  //   });

  //   return items;
  // }, [employeeListData, filters, sortConfig]);

  const getPaginationGroup = () => {
    const totalDisplayed = 5;
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages.filter((p) => p !== 1 && p !== totalPages);
  };

  const handleImport = async (file) => {
    try {
      const res = await dispatch(
        importEmployees({ file, companyId: selectedCompanyId, preview }),
      ).unwrap();
      handlePageChange(currentPage);
      return res;
    } catch (err) {
      return err;
    }
  };

  const handleViewClick = (leave) => {
    toast.info(
      <div style={{ textAlign: 'left' }}>
        <strong style={{ display: 'block', marginBottom: '8px', borderBottom: '1px solid #ccc' }}>
          Leave Details
        </strong>
        <div><strong>Employee:</strong> {leave.employee.firstName} {leave.employee.lastName}</div>
        <div><strong>ID:</strong> {leave.employee.employeeId}</div>
        <div><strong>Dept:</strong> {leave.employee.department?.departmentName}</div>
        <hr />
        <div><strong>Type:</strong> {leave.leaveType.name}</div>
        <div><strong>Period:</strong> {leave.startDate} to {leave.endDate}</div>
        <div><strong>Duration:</strong> {leave.totalDays} days</div>
        <div><strong>Reason:</strong> {leave.reason}</div>
        <div><strong>Status:</strong> <span className="text-uppercase fw-bold">{leave.status}</span></div>
      </div>,
      {
        position: "top-center",
        autoClose: false, // Prevents auto-closing
        closeOnClick: false, // Keeps it open if clicked
        draggable: true,
        progress: undefined,
      }
    );
  };

/* ========================================================================== */
  /* TANSTACK QUERY MUTATIONS FOR ACTIONS                                       */
  /* ========================================================================== */
  const useLeaveAction = (actionFn, successMessage) => {
    return useMutation({
      mutationFn: actionFn,
      onSuccess: () => {
        dispatch(fetchAllLeaveRequests({ status: 'Pending', companyId: storedCompanyId }));
        toast.success(successMessage);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || error?.message || "Operation failed");
      },
    });
  };

  const approveMutation = useLeaveAction((id) => leaveService.approveLeave(id), "Leave approved successfully!");
  const cancelMutation = useLeaveAction((id) => leaveService.cancelLeave(id), "Leave cancelled successfully!");
  const rejectMutation = useLeaveAction((id) => leaveService.rejectLeave(id), "Leave rejected successfully!");

  const handleApprove = (requestId) => approveMutation.mutate(requestId);
  const handleCancel = (requestId) => cancelMutation.mutate(requestId);
  const handleReject = (requestId) => rejectMutation.mutate(requestId);

  return (
    <Layout>
    <ToastContainer position="top-right" autoClose={3000} />
      <div className="w-100 position-relative">
        <style>{`
        .custom-table-header th { 
          background-color: #1e293b !important; color: #ffffff !important; 
          cursor: pointer; user-select: none; transition: background 0.2s;
          font-size: 0.7rem !important; font-weight: 700 !important; 
          letter-spacing: 0.05em !important; text-transform: uppercase !important;
        }
        .custom-table-header th:hover { background-color: #334155 !important; }
        .emp-name-text { font-size: 0.85rem !important; font-weight: 700 !important; color: #1e293b; }
        .emp-sub-text { font-size: 0.75rem !important; color: #64748b; }
        .table-cell-text { font-size: 0.8rem !important; color: #334155; }
        .badge-custom { 
          font-size: 0.65rem !important; font-weight: 700 !important; 
          letter-spacing: 0.02em !important; padding: 6px 12px !important;
        }
        .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; border: 1px solid rgba(0,0,0,0.1); }
        .page-link-custom.active { background: #0f172a !important; color: #fff !important; border-color: #0f172a !important; }
        .filter-input, .status-select { border: 1px solid rgba(0,0,0,0.1); border-radius: 8px; font-size: 0.8rem !important; padding: 10px 14px; background: rgba(255,255,255,0.7); backdrop-filter: blur(5px); outline: none; }
        .items-select { border: 1px solid rgba(0,0,0,0.1); border-radius: 6px; padding: 4px 8px; outline: none; color: #334155; font-weight: 500; }
      `}</style>

        {isLoading && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.3)",
              backdropFilter: "blur(10px)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <div className={isLoading ? "blur-content" : ""}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1
                className="fw-bold text-dark m-0"
                style={{ letterSpacing: "-1.5px" }}
              >
                Employees Leave Approval
              </h1>
              <p className="text-muted small mb-0">Employee Leaves Approval</p>
            </div>
            <div className="d-flex gap-2">
              {/* <button
                className="btn btn-primary px-4 border-0"
                style={{
                  borderRadius: "8px",
                  backgroundColor: "#0f172a",
                  fontWeight: "600",
                  fontSize: "0.85rem",
                }}
                // onClick={() => navigate("/employee/new")}
              >
                <i className="bi bi-gear-fill me-2"></i>Leave Configurations
              </button> */}
            </div>
          </div>

          {/* Filter Selection Row */}
          {/* <div className="row g-2 mb-4">
          <div className="col-md-2">
            <input type="text" className="filter-input w-100" placeholder="Search Name..." value={filters.name} onChange={(e) => setFilters({...filters, name: e.target.value})} />
          </div>
          <div className="col-md-2">
            <input type="text" className="filter-input w-100" placeholder="Search Department..." value={filters.department} onChange={(e) => setFilters({...filters, department: e.target.value})} />
          </div>
          <div className="col-md-3">
             <input type="text" className="filter-input w-100" placeholder="Search Branch..." value={filters.branch} onChange={(e) => setFilters({...filters, branch: e.target.value})} />
          </div>
          <div className="col-md-3">
            <input type="text" className="filter-input w-100" placeholder="Search Company..." value={filters.company} onChange={(e) => setFilters({...filters, company: e.target.value})} />
          </div>
          <div className="col-md-2">
            <select className="status-select w-100" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
              <option value="All">All Status</option>
              {['Active', 'Complete', 'Incomplete', 'Inactive', 'Resigned', 'Separated', 'Terminated', 'AWOL'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div> */}
          <SearchBar
            filters={filters}
            setFilters={setFilters}
            fields={filterSchema}
            onSearch={handleSearch}
          ></SearchBar>

          {/* Core Table Assembly */}
          <div
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              backdropFilter: "blur(12px)",
              borderRadius: "16px",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="custom-table-header">
                  <tr>
                    <th
                      className="py-3 ps-4 border-0"
                      onClick={() => requestSort("firstName")}
                    >
                      EMPLOYEE NAME {getSortIcon("firstName")}
                    </th>
                    <th
                      className="py-3 border-0"
                      onClick={() => requestSort("company")}
                    >
                      company {getSortIcon("company")}
                    </th>
                    <th
                      className="py-3 border-0"
                      onClick={() => requestSort("branch")}
                    >
                      BRANCH {getSortIcon("branch")}
                    </th>
                                        <th
                      className="py-3 border-0"
                      onClick={() => requestSort("department")}
                    >
                      DEPARTMENT {getSortIcon("department")}
                    </th>
                    <th
                      className="py-3 border-0"
                      onClick={() => requestSort("company")}
                    >
                      LEAVE TYPE {getSortIcon("Leave Type")}
                    </th>
                    <th className="py-3 border-0 text-center">STATUS</th>
                    <th className="py-3 border-0 text-center">REASON</th>
                    <th className="py-3 border-0 text-center">START DATE</th>
                    <th className="py-3 border-0 text-center">END DATE</th>
                    <th className="py-3 border-0 text-center">TOTAL DAYS</th>
                    <th
                      className="py-3 border-0"
                      onClick={() => requestSort("createdAt")}
                    >
                      CREATED AT {getSortIcon("createdAt")}
                    </th>
                    <th className="py-3 pe-4 border-0 text-end">ACTIONS</th>
                  </tr>
                </thead>
                <tbody style={{ borderTop: "0" }}>
                  {employeeLeave.map((emp) => {
                    const sStyle = getStatusStyle(emp.employeeStatus);
                    return (
                      <tr key={emp.id} className="align-middle">
                        <td className="ps-4 py-3">
                          <div className="emp-name-text">
                            {emp.employee.firstName} {emp.employee.lastName}
                          </div>
                          <div className="emp-sub-text">
                            ID: {emp.employee.employeeId}-
                            {emp.branch?.BranchCode || "HQ"}
                            {/* <br />
                          {emp.companyEmail || "-"} */}
                          </div>
                        </td>
                             <td className="table-cell-text">
                          {emp.employee.company?.companyName || "-"}
                        </td>
                        <td className="table-cell-text">
                          {emp.employee.branch?.branchName || "-"}
                        </td>
                        <td className="table-cell-text">
                          {emp.employee.department?.departmentName || "-"}
                        </td>
                        <td className="table-cell-text">
                          {emp.leaveType.name || "-"}
                        </td>
         <td className="text-center">
  {(() => {
    // 1. Get the style object based on the status
    const sStyle = getStatusStyle(emp.status);
    
    // 2. Render the badge
    return (
      <span
        className={`badge rounded-pill ${sStyle.bg} bg-opacity-10 ${sStyle.text} border ${sStyle.border} border-opacity-25 badge-custom`}
      >
        {emp.status.charAt(0).toUpperCase() + emp.status.slice(1)}
      </span>
    );
  })()}
</td>
                        {/* <th className="py-3 border-0 text-center">REASON</th>
                  <th className="py-3 border-0 text-center">START DATE</th>
                  <th className="py-3 border-0 text-center">END DATE</th>
                  <th className="py-3 border-0 text-center">TOTAL DAYS</th> */}
                        <td className="table-cell-text">
                          {emp.reason ? emp.reason : "-"}
                        </td>
                        <td className="table-cell-text">
                          {emp.startDate ? emp.startDate : "-"}
                        </td>
                        <td className="table-cell-text">
                          {emp.endDate ? emp.endDate : "-"}
                        </td>
                        <td className="table-cell-text">
                          {emp.totalDays ? emp.totalDays : "-"}
                        </td>
                        <td className="table-cell-text">
                          {emp.createdAt
                            ? new Date(emp.createdAt).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="text-end pe-4">
                          <button
                            className="btn btn-sm btn-outline-primary fw-bold"
                            style={{ fontSize: "0.75rem", borderRadius: "6px" }}
                      onClick={() => handleViewClick(emp)}
                          >
                            View Leave Details
                          </button>
{emp.status === "pending" && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-success fw-bold m-1"
                              style={{
                                fontSize: "0.75rem",
                                borderRadius: "6px",
                              }}
                              onClick={() => handleApprove(emp.id)}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger fw-bold m-1"
                              style={{
                                fontSize: "0.75rem",
                                borderRadius: "6px",
                              }}
                              onClick={() => handleReject(emp.id)}
                            >
                              Reject
                            </button>
                          </>
                        )}

                        {emp.status === "approved" && (
                          <button
                            className="btn btn-sm btn-outline-warning fw-bold m-1"
                            style={{
                              fontSize: "0.75rem",
                              borderRadius: "6px",
                            }}
                            onClick={() => handleCancel(emp.id)}
                          >
                            Cancel
                          </button>
                        )}
                        </td>
                      </tr>
                    );
                  })}
                  {currentData.length === 0 && (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-5 text-muted small"
                      >
                        No employees found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ========================================== */}
            {/* Consolidated & Cleaned Pagination System     */}
            {/* ========================================== */}
            <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 p-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Show
                </span>
                <select
                  className="items-select"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Moving sizes resets back to page 1 safely
                  }}
                >
                  {[10, 25, 50].map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
                <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                  of <strong>{(totalItems || 0).toLocaleString()}</strong>{" "}
                  entries
                </span>
              </div>

              <nav className="d-flex align-items-center">
                {/* Previous Button */}
                <button
                  type="button"
                  className="page-link-custom"
                  onClick={() => {
                    const prevNum = Number(currentPage) - 1;
                    if (prevNum >= 1) handlePageChange(prevNum);
                  }}
                  disabled={Number(currentPage) <= 1}
                  style={{
                    opacity: Number(currentPage) <= 1 ? 0.35 : 1,
                    cursor:
                      Number(currentPage) <= 1 ? "not-allowed" : "pointer",
                  }}
                >
                  <i
                    className="bi bi-chevron-left"
                    style={{
                      fontSize: "0.8rem",
                      stroke: "#64748b",
                      strokeWidth: "1.5px",
                    }}
                  ></i>
                </button>

                {/* Page 1 Button */}
                <button
                  type="button"
                  className={`page-link-custom ${Number(currentPage) === 1 ? "active" : ""}`}
                  onClick={() => handlePageChange(1)}
                >
                  1
                </button>

                {/* Left Break Ellipsis */}
                {Number(currentPage) > 3 && totalPages > 5 && (
                  <span className="mx-2 text-muted">...</span>
                )}

                {/* Dynamic Sliding Numeric Range */}
                {getPaginationGroup().map((p) => {
                  // Prevent rendering duplicate page 1 or totalPages buttons inside the loop
                  if (p === 1 || p === totalPages) return null;
                  return (
                    <button
                      type="button"
                      key={p}
                      className={`page-link-custom ${Number(currentPage) === p ? "active" : ""}`}
                      onClick={() => handlePageChange(p)}
                    >
                      {p}
                    </button>
                  );
                })}

                {/* Right Break Ellipsis */}
                {Number(currentPage) < totalPages - 2 && totalPages > 5 && (
                  <span className="mx-2 text-muted">...</span>
                )}

                {/* Terminal Max Page Button */}
                {totalPages > 1 && (
                  <button
                    type="button"
                    className={`page-link-custom ${Number(currentPage) === totalPages ? "active" : ""}`}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </button>
                )}

                {/* Next Button */}
                <button
                  type="button"
                  className="page-link-custom"
                  onClick={() => {
                    const nextNum = Number(currentPage) + 1;
                    if (nextNum <= totalPages) handlePageChange(nextNum);
                  }}
                  disabled={Number(currentPage) >= totalPages}
                  style={{
                    opacity: Number(currentPage) >= totalPages ? 0.35 : 1,
                    cursor:
                      Number(currentPage) >= totalPages
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <i
                    className="bi bi-chevron-right"
                    style={{
                      fontSize: "0.8rem",
                      stroke: "#64748b",
                      strokeWidth: "1.5px",
                    }}
                  ></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmployeeLeaveApprovalView;
